import { FC, useEffect, useState } from "react";
import { useSettingsStore } from "../../../stores/SettingsStore";
import moment from "moment";
import useDynamicTab from "../../../hooks/useDynamicTab";
import { ExpandableElement } from "../../../components/General/ExpandableElement";
import { Meeting, MeetingAttachment, MeetingAttendance } from "../../../interfaces/Meeting";
import { useIntl } from "react-intl";
import { currencyFormatter, customNumberFormat, customNumberFormatString, getDurationDay } from "../../../functions/general";
import { CustomField } from "../../../interfaces/Settings";
import { returnCustomFieldMeeting, returnCustomFieldMerchant, returnCustomFieldPipeline, returnFile, returnImage, returnLink, returnMeetingCount, returnMerchantNameMeeting, returnMerchantNameMerchant, returnMultiple, returnOption, returnParagraph, returnPipelineCount, returnStatusMeeting, returnWhatsapp } from "../../../components/General/DetailMasterElement";
import { useMeetingStore } from "../../../stores/MeetingsStore";
import useUser from "../../../hooks/User";
import { usePipelineStore } from "../../../stores/PipelineStore";
import useSWR from "swr";
import axios from "axios";
import useAccessToken from "../../../hooks/AccessToken";
import { useParams } from "react-router-dom";
import { useMerchantStore } from "../../../stores/MerchantStore";
import { Merchant } from "../../../interfaces/Merchant";

const MasterDetailMerchant: FC = () => {
    const API_URL = process.env.REACT_APP_API_URL
    const token = useAccessToken()
    const { settings } = useSettingsStore()
    const { multiMerchants } = useMerchantStore();
    const { simplePipelines, multiPipelines } = usePipelineStore()
    const { multiMeetings } = useMeetingStore()
    const user = useUser()
    const { templateMerchantId, merchantId } = useParams()

    const intl = useIntl()
    const [customLead, setCustomLead] = useState({} as any)
    const [customFields, setCustomFields] = useState<CustomField>({})
    const [data, setData] = useState<Merchant>({} as Merchant);
    let result = multiMerchants.find((item: any) => item.id === templateMerchantId);

    const fetcher = (url: string) => axios.get(url, {
        headers: {
            'X-Auth-token': token
        }
    }).then(res => res.data.data)

    const { data: merchantData = {}, isLoading } = useSWR(templateMerchantId && merchantId ? `${API_URL}merchants/${merchantId}` : null, fetcher);

    useEffect(() => {
        if (!isLoading && result) {
            const customData = {
                name: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME' }),
                    value: returnMerchantNameMerchant(data, result)
                },
                ownerName: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC' }),
                    value: data.ownerName
                },
                cityName: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CITY' }),
                    value: data.cityName
                },
                phone: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PHONE' }),
                    value: data.phone
                },
                isWhatsappPhone: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.WHATSAPP' }),
                    value: returnWhatsapp(data)
                },
                email: {
                    name: 'Email',
                    value: data.email || '-'
                },
                lastMeetEnd: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.LAST_MEETING' }),
                    value: data.lastMeetEnd ? getDurationDay(data.lastMeetEnd) : "-"
                },
                lastLeadCreatedTime: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.LAST_ADD_MERCHANT' }, { title: settings.pipeline_title }),
                    value: data.lastLeadCreatedTime ? getDurationDay(data.lastLeadCreatedTime) : "-"
                },
                leadsCount: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.COUNT' }, { title: settings.pipeline_title }),
                    value: returnPipelineCount(data, multiPipelines)
                },
                meetingsCount: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.COUNT' }, { title: intl.formatMessage({ id: 'MENU.MEETINGS' }) }),
                    value: returnMeetingCount(data, multiMeetings)
                },
                creatorName: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.CREATED_BY' }),
                    value: data.creatorName
                },
                dateCreated: {
                    name: intl.formatMessage({ id: 'MASTERS.DATATABLE.TYPE.DATE_CREATED' }),
                    value: moment(data.dateCreated).format("DD MMM YYYY HH:mm")
                },
            }

            const newData = {
                ...customData,
                ...Object.keys(customFields).reduce((acc, key) => {
                    let value;
                    const dataCustomFields = data.customFields[key]
                    const dataInfoCustomFields = (data as any)?.info_custom_fields?.[key];

                    if (customFields[key].type === 'date') {
                        value = dataCustomFields ? moment(dataCustomFields).format("DD MMM YYYY") : "-";
                    } else if (customFields[key].type === 'image' || customFields[key].type === 'images') {
                        value = dataCustomFields ? returnImage(dataCustomFields as string[], intl) : "-";
                    } else if (customFields[key].type === 'file' || customFields[key].type === 'files') {
                        value = dataCustomFields ? returnFile(dataCustomFields, intl) : "-";
                    } else if (customFields[key].type === 'multiple' || customFields[key].type === 'multipleOption') {
                        value = dataCustomFields ? returnMultiple(dataCustomFields as string) : "-";
                    } else if (customFields[key].type === 'link') {
                        value = dataCustomFields ? returnLink(dataCustomFields as string) : "-";
                    } else if (customFields[key].type === 'dateTime') {
                        value = dataCustomFields ? moment(dataCustomFields).format('DD MMM YYYY HH:mm') : '-'
                    } else if (customFields[key].type === 'option') {
                        value = dataCustomFields ? returnOption(dataCustomFields as string) : "-";
                    } else if (customFields[key].type === 'paragraph') {
                        value = dataCustomFields ? returnParagraph(dataCustomFields as string) : "-";
                    } else if (customFields[key].type === 'serial') {
                        value = dataCustomFields ? (dataCustomFields as string[]).join('') : "-";
                    } else if (customFields[key].type === 'merchant') {
                        value = dataInfoCustomFields && dataInfoCustomFields.length > 0 ? dataInfoCustomFields.map((item: any, index: number) => (
                            <>
                                {index > 0 && ', '}
                                {returnCustomFieldMerchant(item)}
                            </>
                        )) : "-";
                    } else if (customFields[key].type === 'user') {
                        value = dataInfoCustomFields ? dataInfoCustomFields.map((item: any) => item.name).join(', ') : "-";
                    } else if (customFields[key].type === 'dateTimeRange') {
                        value = dataCustomFields ? (dataCustomFields as string[]).map((item: any) => moment(item).format("DD MMM YYYY HH:mm")).join(' - ') : '-';
                    } else if (customFields[key].type === 'pipeline') {
                        let result: any = simplePipelines.find((item: any) => item.id === (dataInfoCustomFields?.[0] as any)?.id)

                        if (result) {
                            let name = result.name + " / " + result.nominal + " / " + moment(result.dateCreated).format('DD MMM YYYY')
                            let idTemplate = (simplePipelines as any).find((item: any) => item.id === result.id)?.leadTemplateId
                            let idPipeline = result.id
                            value = returnCustomFieldPipeline(idTemplate!, idPipeline, name);
                        } else value = "-"
                    } else if (customFields[key].type === 'meeting') {
                        value = dataInfoCustomFields && dataInfoCustomFields.length > 0 ? dataInfoCustomFields.map((item: any, index: number) => (
                            <>
                                {index > 0 && ', '}
                                {returnCustomFieldMeeting(item)}
                            </>
                        )) : "-";
                    } else if (customFields[key].type === 'time') {
                        let result = dataCustomFields ? moment(dataCustomFields).format('HH:mm') : '-'
                        value = result
                    } else if (customFields[key].type === 'number') {
                        value = dataCustomFields ? customNumberFormatString((dataCustomFields.toString())) : "-";
                    } else if (customFields[key].type === 'product') {
                        value = dataInfoCustomFields ? dataInfoCustomFields.map((item: any) => item.product_name).join(', ') : "-";
                    }
                    else {
                        value = dataCustomFields ? dataCustomFields : "-";
                    }

                    return {
                        ...acc,
                        [key]: {
                            name: customFields[key].name,
                            value
                        }
                    };
                }, {})
            };

            setCustomLead(newData);
        }
    }, [customFields, data, result, multiPipelines, multiMeetings])

    useEffect(() => {
        if (result && Object.keys(result).length !== 0) {
            setCustomFields(result.customFields);
        }
    }, [result])

    useEffect(() => {
        if (!isLoading && Object.keys(merchantData).length !== 0) {
            setData(merchantData)
        }
    }, [merchantData])

    useDynamicTab({ title: `Detail ${settings.merchant_title} | CRM WooBlazz` });

    return (
        <>
            <ol className="breadcrumb text-muted fs-6 fw-bold">
                <li className="breadcrumb-item text-dark">Detail {settings.merchant_title}</li>
            </ol>

            <div className="card mt-8">
                <div className="card-body">
                    {
                        isLoading ?
                            (
                                <div className="loading-overlay">
                                    <div className="loader"></div>
                                </div>
                            )
                            :
                            <div className="row">
                                {
                                    Object.keys(customLead)?.map((key, index) => {
                                        return (
                                            <div className="row pb-2 col-12 col-lg-6 col-xl-6">
                                                <div className="col-3 text-end" style={{ fontWeight: "500", color: "#767373" }}>{customLead[key]?.name}</div>
                                                <div className="col-9" style={{ fontWeight: "500", color: "black" }}>{customLead[key]?.value}</div>
                                            </div>
                                        )
                                    }) || <></>
                                }
                            </div>
                    }
                </div>
            </div>
        </>
    )
}

export { MasterDetailMerchant }